import { getScope, signal } from '@zyra/core';
import { h, type Child, type HTMLAttributes, type Props } from '@zyra/dom';
export function createRouter(options: { focus?: () => HTMLElement | null; title?: (path: string) => string; transitions?: boolean } = {}) {
  const path = signal(window.location.pathname + window.location.search + window.location.hash);
  let disposed = false;
  const focus = (scroll: boolean) => queueMicrotask(() => {
    if (disposed) return;
    let anchor: HTMLElement | null = null;
    try { if (location.hash) anchor = document.getElementById(decodeURIComponent(location.hash.slice(1))); } catch {}
    const target = anchor ?? options.focus?.() ?? document.querySelector<HTMLElement>('main h1') ?? document.querySelector<HTMLElement>('main');
    if (target) {
      if (!target.hasAttribute('tabindex')) target.tabIndex = -1;
      target.focus({ preventScroll: true });
    }
    if (scroll) { if (anchor) anchor.scrollIntoView(); else window.scrollTo({ top: 0, left: 0, behavior: 'instant' }); }
  });
  const update = () => {
    if (disposed) return;
    path.value = window.location.pathname + window.location.search + window.location.hash;
    if (options.title) document.title = options.title(path.value);
  };
  const pop = () => { update(); focus(false); };
  window.addEventListener('popstate', pop);
  const dispose = () => { if (!disposed) { disposed = true; window.removeEventListener('popstate', pop); } };
  getScope()?.add(dispose);
  function navigate(to: string, settings: { replace?: boolean; scroll?: boolean } = {}) {
    if (disposed) throw new Error('Router is disposed');
    const url = new URL(to, location.href);
    if (url.origin !== location.origin || url.username || url.password) throw new Error('Router navigation must stay on the current origin');
    history[settings.replace ? 'replaceState' : 'pushState']({}, '', url);
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (options.transitions && !reduced && document.startViewTransition) {
      const transition = document.startViewTransition(update);
      void transition.finished.then(() => focus(settings.scroll !== false), () => focus(settings.scroll !== false));
    } else { update(); focus(settings.scroll !== false); }
  }
  return {
    path, navigate, dispose,
    Route(props: { path: string; children?: Child }) { return () => path.value.split(/[?#]/)[0] === props.path ? props.children : null; },
    Link(props: Omit<HTMLAttributes<'a'>, 'download'> & { href: string; download?: string | boolean }): Child {
      const forwarded: Props = {};
      for (const key of Object.keys(props)) if (key !== 'onClick') Object.defineProperty(forwarded, key, { enumerable: true, get: () => (props as unknown as Props)[key] });
      forwarded.onClick = (event: MouseEvent) => {
        props.onClick?.(event as MouseEvent & { currentTarget: HTMLAnchorElement });
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey ||
            (props.target && props.target !== '_self') || (props.download !== undefined && props.download !== false)) return;
        if (new URL(props.href, location.href).origin !== location.origin) return;
        event.preventDefault(); navigate(props.href);
      };
      return h('a', forwarded);
    }
  };
}
